import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  Mountain,
  Pencil,
  Plus,
  Star,
  Trash2,
  Upload,
  Users,
  X,
  XCircle
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { MeasurementInput, MeasurementValue } from '../components/ui/measurement'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import { useMeasurements } from '../hooks/useMeasurements'
import {
  hikeInputToMetric,
  metricToHikeInput,
  formatInputNumber,
  fractionDigitsFor,
  metricToDisplay,
  type MeasurementKind,
  type MeasurementUnit
} from '../lib/measurements'
import type { ItineraryDay } from '../lib/api/tourOperators'
import { HIKE_DIFFICULTY_LIST_TEXT, formatHikeDifficulty, DEFAULT_HIKE_DIFFICULTY, HIKE_DIFFICULTY_OPTIONS, parseHikeDifficulty, parseHikeDifficultyMode, type HikeDifficulty, type HikeDifficultyMode } from '../lib/hikeDifficulty'
import {
  DEFAULT_PLAN_GENERATION_PROFILE,
  PLAN_GENERATION_PROFILES,
  parsePlanGenerationProfile,
  planGenerationProfileLabel,
  type PlanGenerationProfile
} from '../lib/planGenerationProfile'
import {
  createPopularHike,
  deletePopularHike,
  deletePopularHikeHeroImage,
  getPopularHikes,
  regenerateAllPopularHikeThumbnails,
  regeneratePopularHikeThumbnails,
  resetAllPopularHikes,
  uploadPopularHikeHeroImage,
  PopularHike,
  PopularHikePayload,
  updatePopularHike
} from '../lib/api/popularHikes'
import {
  syncAllPopularHikeGroups,
  syncPopularHikeGroup,
  type AdminGroupSyncResult
} from '../lib/api/social'
import { CSV_SCHEMA_TYPE_COLUMN, CSV_SCHEMA_VERSION_COLUMN, validateCsvSchemaFromMaps } from '../utils/csv'

const MAX_ITINERARY_DAYS = 16

const HikeThumbnail = ({ source }: { source?: string | null }) => {
  const [loadedSource, setLoadedSource] = useState<string | null>(null)
  return (
    <span aria-hidden="true" className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/5 text-primary/60">
      <Mountain className="h-5 w-5" strokeWidth={1.5} />
      {source && <img src={source} alt="" loading="lazy" onLoad={() => setLoadedSource(source)} onError={() => setLoadedSource(null)} className={`absolute inset-0 h-full w-full object-cover transition-opacity ${loadedSource === source ? 'opacity-100' : 'opacity-0'}`} />}
    </span>
  )
}

const POPULAR_HIKE_CSV_TYPE = 'PopularHikes'
const POPULAR_HIKE_CSV_VERSION = 2

const POPULAR_HIKE_STATIC_COLUMNS = [
  CSV_SCHEMA_TYPE_COLUMN,
  CSV_SCHEMA_VERSION_COLUMN,
  'source measurement unit',
  'tour operator ID',
  'tour operator name',
  'trip name',
  'description',
  'link to itinerary',
  'link to map',
  'UN geoscheme',
  'Country',
  'State',
  'Geogrphic Region',
  'City',
  'Protected Area',
  'trip ID',
  'difficulty level',
  'difficulty mode',
  'plan generation profile',
  'pack weight',
  'highest point',
  'favorite',
  'above 8k feet',
  'trip number of days',
  'multiple daily options',
  'total elevation gain meters',
  'total elevation gain feet',
  'total elevation loss meters',
  'total elevation loss feet',
  'total distance km',
  'total distance miles',
  'total trip distance km',
  'total trip distance miles',
  'total trip elevation gain meters',
  'total trip elevation gain feet'
] as const

const POPULAR_HIKE_DAY_COLUMNS = Array.from({ length: MAX_ITINERARY_DAYS }, (_, index) => {
  const day = index + 1
  return [
    `day ${day} distance km`,
    `day ${day} distance miles`,
    `day ${day} elevation gain meters`,
    `day ${day} elevation gain feet`,
    `day ${day} elevation loss meters`,
    `day ${day} elevation loss feet`
  ]
}).flat()

const POPULAR_HIKE_COLUMNS = [...POPULAR_HIKE_STATIC_COLUMNS, ...POPULAR_HIKE_DAY_COLUMNS]

// Normalized (lower-case) CSV keys for the whole-trip totals that LongItinerary hikes carry.
const TRIP_DISTANCE_KM_KEYS = ['total trip distance km', 'total trip distance']
const TRIP_DISTANCE_MILES_KEYS = ['total trip distance miles', 'total trip distance mi']
const TRIP_ELEVATION_GAIN_METERS_KEYS = ['total trip elevation gain meters', 'total trip elevation gain m', 'total trip elevation gain']
const TRIP_ELEVATION_GAIN_FEET_KEYS = ['total trip elevation gain feet', 'total trip elevation gain ft']
const TRIP_TOTAL_KEYS = [
  ...TRIP_DISTANCE_KM_KEYS,
  ...TRIP_DISTANCE_MILES_KEYS,
  ...TRIP_ELEVATION_GAIN_METERS_KEYS,
  ...TRIP_ELEVATION_GAIN_FEET_KEYS
]

type CsvValueMap = Record<string, string>

type ParsedCsvRow = {
  id: string
  rowNumber: number
  normalized: CsvValueMap
}

type ImportStatus = {
  state: 'pending' | 'success' | 'error'
  message?: string
}

type PopularHikeForm = {
  operatorId: string
  operatorName: string
  tripName: string
  description: string
  linkToItinerary: string
  linkToMap: string
  unGeoscheme: string
  country: string
  stateOrProvince: string
  geographicRegion: string
  city: string
  protectedArea: string
  tripExternalId: string
  difficulty: string
  difficultyLevel: HikeDifficulty
  difficultyMode: HikeDifficultyMode
  planGenerationProfile: PlanGenerationProfile
  packWeight: string
  highestPoint: string
  aboveEightThousandFeet: '' | 'yes' | 'no'
  tripNumberOfDays: string
  multipleDailyOptions: '' | 'yes' | 'no'
  totalElevationGain: string
  totalElevationLoss: string
  totalDistance: string
  totalTripDistance: string
  totalTripElevationGain: string
  sourceMeasurementUnit: MeasurementUnit | ''
  isFavorite: boolean
  itinerary: ItineraryDay[]
  heroImageUrls: string[]
}

type SortDirection = 'asc' | 'desc'
type HikeSortKey = 'tripName' | 'country' | 'tripNumberOfDays' | 'difficulty' | 'totalDistanceKm' | 'totalElevationGainMeters' | 'totalElevationLossMeters' | 'isFavorite'
type HikeSortConfig = { key: HikeSortKey; direction: SortDirection }

type ImportPreviewRow = {
  id: string
  rowNumber: number
  operatorName: string
  tripName: string
  tripExternalId?: string
  payload: PopularHikePayload
  matchedHike?: PopularHike
  errors: string[]
  action: 'create' | 'update' | 'blocked'
  status?: ImportStatus
}

const normalizeKey = (key: string) => key.trim().toLowerCase()

const normalizeString = (value?: string | null) => value?.trim().toLowerCase() ?? ''

const parseNumberCell = (value?: string) => {
  if (!value) {
    return undefined
  }
  const trimmed = value.trim()
  if (!trimmed || trimmed.toLowerCase() === 'n/a') {
    return undefined
  }
  const sanitized = trimmed.replace(/,/g, '')
  const numericCandidate = sanitized.replace(/[^0-9.-]/g, '')
  if (!numericCandidate) {
    return undefined
  }
  const parsed = Number(numericCandidate)
  return Number.isFinite(parsed) ? parsed : undefined
}

const parseBooleanCell = (value?: string) => {
  if (!value) {
    return null
  }
  const normalized = value.trim().toLowerCase()
  if (['y', 'yes', 'true', '1'].includes(normalized)) {
    return true
  }
  if (['n', 'no', 'false', '0'].includes(normalized)) {
    return false
  }
  return null
}

const boolToFlag = (value?: boolean | null): '' | 'yes' | 'no' => {
  if (value === true) {
    return 'yes'
  }
  if (value === false) {
    return 'no'
  }
  return ''
}

const flagToBool = (flag: '' | 'yes' | 'no') => {
  if (flag === 'yes') {
    return true
  }
  if (flag === 'no') {
    return false
  }
  return null
}

const parseNumberField = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const parseIntegerField = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) {
    return null
  }
  return Math.round(parsed)
}

const metricFormString = (value: number | null) => (value == null ? '' : value.toString())

const firstNumberCell = (map: CsvValueMap, keys: string[]) => {
  for (const key of keys) {
    const parsed = parseNumberCell(map[key])
    if (parsed !== undefined) {
      return parsed
    }
  }
  return undefined
}

const parseMeasurementUnitCell = (value?: string): MeasurementUnit | undefined => {
  const normalized = value?.trim().toLowerCase()
  if (!normalized) {
    return undefined
  }
  if (['imperial', 'us', 'u.s.', 'mile', 'miles', 'mi', 'foot', 'feet', 'ft'].includes(normalized)) {
    return 'Imperial'
  }
  if (['metric', 'kilometer', 'kilometers', 'kilometre', 'kilometres', 'km', 'meter', 'meters', 'metre', 'metres', 'm'].includes(normalized)) {
    return 'Metric'
  }
  return undefined
}

const resolveSourceMeasurementUnit = (
  explicitUnit: MeasurementUnit | undefined,
  imperialValues: Array<number | undefined>,
  metricValues: Array<number | undefined>
): MeasurementUnit | undefined => {
  if (explicitUnit) {
    return explicitUnit
  }
  if (imperialValues.some((value) => value !== undefined)) {
    return 'Imperial'
  }
  if (metricValues.some((value) => value !== undefined)) {
    return 'Metric'
  }
  return undefined
}

const escapeCsvValue = (value: string) => {
  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

export const buildItineraryFromCsv = (map: CsvValueMap): ItineraryDay[] => {
  const days: ItineraryDay[] = []
  for (let day = 1; day <= MAX_ITINERARY_DAYS; day += 1) {
    const explicitUnit = parseMeasurementUnitCell(
      map[`day ${day} source measurement unit`] ??
      map[`day ${day} measurement unit`] ??
      map[`day ${day} source unit`]
    )
    const distanceMiles = firstNumberCell(map, [`day ${day} distance miles`, `day ${day} distance mi`])
    const gainFeet = firstNumberCell(map, [`day ${day} elevation gain feet`, `day ${day} elevation gain ft`])
    const lossFeet = firstNumberCell(map, [`day ${day} elevation loss feet`, `day ${day} elevation loss ft`])
    const distanceKm = firstNumberCell(map, [`day ${day} distance km`, `day ${day} distance`])
    const gainMeters = firstNumberCell(map, [`day ${day} elevation gain meters`, `day ${day} elevation gain m`, `day ${day} elevation gain`])
    const lossMeters = firstNumberCell(map, [`day ${day} elevation loss meters`, `day ${day} elevation loss m`, `day ${day} elevation loss`])
    const distance = distanceMiles === undefined
      ? distanceKm
      : hikeInputToMetric(distanceMiles, 'distance', 'Imperial')
    const gain = gainFeet === undefined
      ? gainMeters
      : hikeInputToMetric(gainFeet, 'elevation', 'Imperial')
    const loss = lossFeet === undefined
      ? lossMeters
      : hikeInputToMetric(lossFeet, 'elevation', 'Imperial')
    if (distance === undefined && gain === undefined && loss === undefined) {
      continue
    }
    days.push({
      dayNumber: day,
      distanceKm: distance,
      elevationGainMeters: gain,
      elevationLossMeters: loss,
      sourceMeasurementUnit: resolveSourceMeasurementUnit(
        explicitUnit,
        [distanceMiles, gainFeet, lossFeet],
        [distanceKm, gainMeters, lossMeters]
      )
    })
  }
  return days
}

const tokenizeCsv = (text: string) => {
  const rows: string[][] = []
  let current = ''
  let row: string[] = []
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]
    if (char === '"') {
      if (inQuotes && text[index + 1] === '"') {
        current += '"'
        index += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }
    if (char === ',' && !inQuotes) {
      row.push(current)
      current = ''
      continue
    }
    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && text[index + 1] === '\n') {
        index += 1
      }
      row.push(current)
      rows.push(row)
      row = []
      current = ''
      continue
    }
    current += char
  }

  if (inQuotes) {
    throw new Error('CSV ended while inside a quoted field.')
  }

  if (current.length > 0 || row.length > 0) {
    row.push(current)
    rows.push(row)
  }

  return rows
}

const parseCsvText = (text: string): ParsedCsvRow[] => {
  const rows = tokenizeCsv(text)
  if (rows.length === 0) {
    throw new Error('CSV file is empty.')
  }

  const headerRow = rows.shift()!
  headerRow[0] = headerRow[0].replace(/^\ufeff/, '')
  const headers = headerRow.map((header) => header.trim())

  return rows
    .map((values, index) => {
      const normalized: CsvValueMap = {}
      let hasContent = false
      headers.forEach((header, headerIndex) => {
        const cell = (values[headerIndex] ?? '').trim()
        normalized[normalizeKey(header)] = cell
        if (cell) {
          hasContent = true
        }
      })
      if (!hasContent) {
        return null
      }
      return {
        id: `csv-${index}-${Math.random().toString(36).slice(2, 8)}`,
        rowNumber: index + 2,
        normalized
      }
    })
    .filter((row): row is ParsedCsvRow => Boolean(row))
}

const createEmptyItineraryDay = (dayNumber: number): ItineraryDay => ({
  dayNumber,
  distanceKm: undefined,
  elevationGainMeters: undefined,
  elevationLossMeters: undefined
})

const createEmptyForm = (): PopularHikeForm => ({
  operatorId: '',
  operatorName: '',
  tripName: '',
  description: '',
  linkToItinerary: '',
  linkToMap: '',
  unGeoscheme: '',
  country: '',
  stateOrProvince: '',
  geographicRegion: '',
  city: '',
  protectedArea: '',
  tripExternalId: '',
  difficulty: '',
  difficultyLevel: DEFAULT_HIKE_DIFFICULTY,
  difficultyMode: 'Calculated',
  planGenerationProfile: DEFAULT_PLAN_GENERATION_PROFILE,
  packWeight: '',
  highestPoint: '',
  aboveEightThousandFeet: '',
  tripNumberOfDays: '',
  multipleDailyOptions: '',
  totalElevationGain: '',
  totalElevationLoss: '',
  totalDistance: '',
  totalTripDistance: '',
  totalTripElevationGain: '',
  sourceMeasurementUnit: '',
  isFavorite: false,
  heroImageUrls: [],
  itinerary: [createEmptyItineraryDay(1)]
})

const mapHikeToForm = (hike: PopularHike): PopularHikeForm => ({
  operatorId: hike.operatorId ?? '',
  operatorName: hike.operatorName ?? '',
  tripName: hike.tripName,
  description: hike.description ?? '',
  linkToItinerary: hike.linkToItinerary ?? '',
  linkToMap: hike.linkToMap ?? '',
  unGeoscheme: hike.unGeoscheme ?? '',
  country: hike.country ?? '',
  stateOrProvince: hike.stateOrProvince ?? '',
  geographicRegion: hike.geographicRegion ?? '',
  city: hike.city ?? '',
  protectedArea: hike.protectedArea ?? '',
  tripExternalId: hike.tripExternalId ?? '',
  difficulty: hike.difficulty ?? '',
  difficultyLevel: parseHikeDifficulty(hike.difficultyLevel) ?? DEFAULT_HIKE_DIFFICULTY,
  difficultyMode: hike.difficultyMode ?? 'Legacy',
  planGenerationProfile: hike.planGenerationProfile ?? DEFAULT_PLAN_GENERATION_PROFILE,
  packWeight: hike.packWeight ?? '',
  highestPoint: hike.highestPoint ?? '',
  aboveEightThousandFeet: boolToFlag(hike.aboveEightThousandFeet),
  tripNumberOfDays: hike.tripNumberOfDays?.toString() ?? '',
  multipleDailyOptions: boolToFlag(hike.multipleDailyOptions),
  totalElevationGain: hike.totalElevationGainMeters?.toString() ?? '',
  totalElevationLoss: hike.totalElevationLossMeters?.toString() ?? '',
  totalDistance: hike.totalDistanceKm?.toString() ?? '',
  totalTripDistance: hike.totalTripDistanceKm?.toString() ?? '',
  totalTripElevationGain: hike.totalTripElevationGainMeters?.toString() ?? '',
  sourceMeasurementUnit: hike.sourceMeasurementUnit ?? '',
  isFavorite: Boolean(hike.isFavorite),
  heroImageUrls: hike.heroImageUrls ?? [],
  itinerary: hike.itinerary.length > 0 ? hike.itinerary : [createEmptyItineraryDay(1)]
})

const sumItinerary = (days: ItineraryDay[]) => {
  let distance = 0
  let gain = 0
  let loss = 0
  let hasAny = false
  for (const day of days) {
    if (day.distanceKm != null) { distance += day.distanceKm; hasAny = true }
    if (day.elevationGainMeters != null) { gain += day.elevationGainMeters; hasAny = true }
    if (day.elevationLossMeters != null) { loss += day.elevationLossMeters; hasAny = true }
  }
  if (!hasAny) return { distance: null, gain: null, loss: null }
  return {
    distance: distance || null,
    gain: gain || null,
    loss: loss || null
  }
}

const mapFormToPayload = (form: PopularHikeForm): PopularHikePayload => {
  const sourceMeasurementUnit = form.sourceMeasurementUnit || null
  const itinerary = form.itinerary.map((day, index) => ({
    dayNumber: day.dayNumber || index + 1,
    distanceKm: day.distanceKm ?? null,
    elevationGainMeters: day.elevationGainMeters ?? null,
    elevationLossMeters: day.elevationLossMeters ?? null,
    sourceMeasurementUnit: day.sourceMeasurementUnit ?? sourceMeasurementUnit
  }))
  const totals = sumItinerary(itinerary)
  const isLongItinerary = form.planGenerationProfile === 'LongItinerary'
  return {
    operatorId: form.operatorId.trim() || null,
    operatorName: form.operatorName.trim() || null,
    tripName: form.tripName.trim(),
    description: form.description.trim() || null,
    linkToItinerary: form.linkToItinerary.trim() || null,
    linkToMap: form.linkToMap.trim() || null,
    unGeoscheme: form.unGeoscheme.trim() || null,
    country: form.country.trim() || null,
    stateOrProvince: form.stateOrProvince.trim() || null,
    geographicRegion: form.geographicRegion.trim() || null,
    city: form.city.trim() || null,
    protectedArea: form.protectedArea.trim() || null,
    tripExternalId: form.tripExternalId.trim() || null,
    difficulty: form.difficulty.trim() || null,
    difficultyLevel: form.difficultyMode === 'Manual' ? form.difficultyLevel : null,
    difficultyMode: form.difficultyMode,
    planGenerationProfile: form.planGenerationProfile,
    packWeight: form.packWeight.trim() || null,
    highestPoint: form.highestPoint.trim() || null,
    aboveEightThousandFeet: flagToBool(form.aboveEightThousandFeet),
    tripNumberOfDays: parseIntegerField(form.tripNumberOfDays),
    multipleDailyOptions: flagToBool(form.multipleDailyOptions),
    totalElevationGainMeters: totals.gain ?? parseNumberField(form.totalElevationGain),
    totalElevationLossMeters: totals.loss ?? parseNumberField(form.totalElevationLoss),
    totalDistanceKm: totals.distance ?? parseNumberField(form.totalDistance),
    totalTripDistanceKm: isLongItinerary ? parseNumberField(form.totalTripDistance) : null,
    totalTripElevationGainMeters: isLongItinerary ? parseNumberField(form.totalTripElevationGain) : null,
    sourceMeasurementUnit,
    isFavorite: form.isFavorite,
    // heroImageUrls are managed via dedicated API; keep them on the entity, not via bulk payload
    itinerary
  }
}

// Mirrors PopularHikeUpsertDto.ValidateLongItinerary so the content team sees problems before the server rejects the save.
const LONG_ITINERARY_DISTANCE_TOLERANCE_KM = 0.05
const LONG_ITINERARY_GAIN_TOLERANCE_METERS = 0.5

export type LongItineraryErrors = Partial<Record<'itinerary' | 'tripNumberOfDays' | 'totalTripDistanceKm' | 'totalTripElevationGainMeters', string>>

export const validateLongItinerary = (payload: PopularHikePayload): LongItineraryErrors => {
  if (payload.planGenerationProfile !== 'LongItinerary') {
    return {}
  }
  const dayCount = payload.itinerary.length
  if (dayCount === 0) {
    return { itinerary: 'A Long Itinerary hike needs at least one itinerary day.' }
  }
  const errors: LongItineraryErrors = {}
  if (payload.tripNumberOfDays == null || payload.tripNumberOfDays <= dayCount) {
    errors.tripNumberOfDays = `Trip days must be greater than the ${dayCount} itinerary day${dayCount === 1 ? '' : 's'} entered.`
  }
  const itineraryDistanceKm = payload.itinerary.reduce((sum, day) => sum + (day.distanceKm ?? 0), 0)
  if (payload.totalTripDistanceKm == null || payload.totalTripDistanceKm + LONG_ITINERARY_DISTANCE_TOLERANCE_KM < itineraryDistanceKm) {
    errors.totalTripDistanceKm = 'Total trip distance is required and cannot be less than the itinerary total.'
  }
  const itineraryGainMeters = payload.itinerary.reduce((sum, day) => sum + (day.elevationGainMeters ?? 0), 0)
  if (payload.totalTripElevationGainMeters == null || payload.totalTripElevationGainMeters + LONG_ITINERARY_GAIN_TOLERANCE_METERS < itineraryGainMeters) {
    errors.totalTripElevationGainMeters = 'Total trip elevation gain is required and cannot be less than the itinerary total.'
  }
  return errors
}

const formatNumberValue = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return ''
  }
  return `${value}`
}

const formatNativeNumberValue = (
  valueMetric: number | null | undefined,
  kind: MeasurementKind,
  unit: MeasurementUnit
) => {
  if (valueMetric === null || valueMetric === undefined || Number.isNaN(valueMetric)) {
    return ''
  }

  if ((kind === 'distance' || kind === 'elevation') && unit === 'Imperial') {
    return formatInputNumber(metricToHikeInput(valueMetric, kind, unit), 6)
  }

  const value = metricToDisplay(valueMetric, kind, unit)
  return formatInputNumber(value, fractionDigitsFor(kind, unit))
}

const formatBooleanCsv = (value?: boolean | null) => {
  if (value === null || value === undefined) {
    return ''
  }
  return value ? 'Y' : 'N'
}

export const buildCsvForPopularHikes = (hikes: PopularHike[]) => {
  const rows: string[] = []

  hikes.forEach((hike) => {
    const itinerary = [...hike.itinerary].sort((a, b) => a.dayNumber - b.dayNumber)
    const sourceMeasurementUnit = hike.sourceMeasurementUnit ?? 'Metric'
    const usesImperialTotals = sourceMeasurementUnit === 'Imperial'
    const map: Record<string, string> = {
      [CSV_SCHEMA_TYPE_COLUMN]: POPULAR_HIKE_CSV_TYPE,
      [CSV_SCHEMA_VERSION_COLUMN]: POPULAR_HIKE_CSV_VERSION.toString(),
      'source measurement unit': sourceMeasurementUnit,
      'tour operator ID': hike.operatorId ?? '',
      'tour operator name': hike.operatorName ?? '',
      'trip name': hike.tripName,
      description: hike.description ?? '',
      'link to itinerary': hike.linkToItinerary ?? '',
      'link to map': hike.linkToMap ?? '',
      'UN geoscheme': hike.unGeoscheme ?? '',
      Country: hike.country ?? '',
      State: hike.stateOrProvince ?? '',
      'Geogrphic Region': hike.geographicRegion ?? '',
      City: hike.city ?? '',
      'Protected Area': hike.protectedArea ?? '',
      'trip ID': hike.tripExternalId ?? '',
      'difficulty level': parseHikeDifficulty(hike.difficultyLevel) ?? '',
      'difficulty mode': hike.difficultyMode ?? 'Legacy',
      'plan generation profile': hike.planGenerationProfile ?? DEFAULT_PLAN_GENERATION_PROFILE,
      'pack weight': hike.packWeight ?? '',
      'highest point': hike.highestPoint ?? '',
      favorite: formatBooleanCsv(hike.isFavorite),
      'above 8k feet': formatBooleanCsv(hike.aboveEightThousandFeet),
      'trip number of days': hike.tripNumberOfDays?.toString() ?? (itinerary.length ? `${itinerary.length}` : ''),
      'multiple daily options': formatBooleanCsv(hike.multipleDailyOptions),
      'total elevation gain meters': usesImperialTotals ? '' : formatNumberValue(hike.totalElevationGainMeters),
      'total elevation gain feet': usesImperialTotals ? formatNativeNumberValue(hike.totalElevationGainMeters, 'elevation', 'Imperial') : '',
      'total elevation loss meters': usesImperialTotals ? '' : formatNumberValue(hike.totalElevationLossMeters),
      'total elevation loss feet': usesImperialTotals ? formatNativeNumberValue(hike.totalElevationLossMeters, 'elevation', 'Imperial') : '',
      'total distance km': usesImperialTotals ? '' : formatNumberValue(hike.totalDistanceKm),
      'total distance miles': usesImperialTotals ? formatNativeNumberValue(hike.totalDistanceKm, 'distance', 'Imperial') : '',
      'total trip distance km': usesImperialTotals ? '' : formatNumberValue(hike.totalTripDistanceKm),
      'total trip distance miles': usesImperialTotals ? formatNativeNumberValue(hike.totalTripDistanceKm, 'distance', 'Imperial') : '',
      'total trip elevation gain meters': usesImperialTotals ? '' : formatNumberValue(hike.totalTripElevationGainMeters),
      'total trip elevation gain feet': usesImperialTotals ? formatNativeNumberValue(hike.totalTripElevationGainMeters, 'elevation', 'Imperial') : ''
    }

    for (let day = 1; day <= MAX_ITINERARY_DAYS; day += 1) {
      const entry = itinerary.find((d) => d.dayNumber === day)
      const dayUnit = entry?.sourceMeasurementUnit ?? sourceMeasurementUnit
      const usesImperialDay = dayUnit === 'Imperial'
      map[`day ${day} distance km`] = usesImperialDay ? '' : formatNumberValue(entry?.distanceKm)
      map[`day ${day} distance miles`] = usesImperialDay ? formatNativeNumberValue(entry?.distanceKm, 'distance', 'Imperial') : ''
      map[`day ${day} elevation gain meters`] = usesImperialDay ? '' : formatNumberValue(entry?.elevationGainMeters)
      map[`day ${day} elevation gain feet`] = usesImperialDay ? formatNativeNumberValue(entry?.elevationGainMeters, 'elevation', 'Imperial') : ''
      map[`day ${day} elevation loss meters`] = usesImperialDay ? '' : formatNumberValue(entry?.elevationLossMeters)
      map[`day ${day} elevation loss feet`] = usesImperialDay ? formatNativeNumberValue(entry?.elevationLossMeters, 'elevation', 'Imperial') : ''
    }

    const row = POPULAR_HIKE_COLUMNS.map((column) => escapeCsvValue(map[column] ?? ''))
    rows.push(row.join(','))
  })

  return [POPULAR_HIKE_COLUMNS.join(','), ...rows].join('\n')
}

const makeCompositeKey = (tripName?: string | null) => {
  const tripPart = normalizeString(tripName)
  if (!tripPart) {
    return null
  }
  return tripPart
}

type PopularHikeLookups = {
  byTripId: Map<string, PopularHike>
  byComposite: Map<string, PopularHike>
}

const buildLookups = (hikes: PopularHike[]): PopularHikeLookups => {
  const byTripId = new Map<string, PopularHike>()
  const byComposite = new Map<string, PopularHike>()

  hikes.forEach((hike) => {
    const tripId = hike.tripExternalId?.trim()
    if (tripId) {
      byTripId.set(tripId, hike)
    }
    const composite = makeCompositeKey(hike.tripName)
    if (composite) {
      byComposite.set(composite, hike)
    }
  })

  return { byTripId, byComposite }
}

export const buildPayloadFromCsv = (map: CsvValueMap): PopularHikePayload => {
  const itinerary = buildItineraryFromCsv(map)
  const totalElevationGainFeet = firstNumberCell(map, ['total elevation gain feet', 'total elevation gain ft'])
  const totalElevationLossFeet = firstNumberCell(map, ['total elevation loss feet', 'total elevation loss ft'])
  const totalDistanceMiles = firstNumberCell(map, ['total distance miles', 'total distance mi'])
  const totalElevationGainMetric = firstNumberCell(map, ['total elevation gain meters', 'total elevation gain m', 'total elevation gain'])
  const totalElevationLossMetric = firstNumberCell(map, ['total elevation loss meters', 'total elevation loss m', 'total elevation loss'])
  const totalDistanceMetric = firstNumberCell(map, ['total distance km', 'total distance'])
  const totalTripDistanceMiles = firstNumberCell(map, TRIP_DISTANCE_MILES_KEYS)
  const totalTripElevationGainFeet = firstNumberCell(map, TRIP_ELEVATION_GAIN_FEET_KEYS)
  const totalTripDistanceMetric = firstNumberCell(map, TRIP_DISTANCE_KM_KEYS)
  const totalTripElevationGainMetric = firstNumberCell(map, TRIP_ELEVATION_GAIN_METERS_KEYS)
  const sourceMeasurementUnit = resolveSourceMeasurementUnit(
    parseMeasurementUnitCell(map['source measurement unit'] ?? map['measurement unit'] ?? map['source unit']),
    [totalDistanceMiles, totalElevationGainFeet, totalElevationLossFeet],
    [totalDistanceMetric, totalElevationGainMetric, totalElevationLossMetric]
  ) ?? (
    itinerary.some((day) => day.sourceMeasurementUnit === 'Imperial')
      ? 'Imperial'
      : itinerary.some((day) => day.sourceMeasurementUnit === 'Metric')
        ? 'Metric'
        : null
  )
  const itineraryWithSource = itinerary.map((day) => ({
    ...day,
    sourceMeasurementUnit: day.sourceMeasurementUnit ?? sourceMeasurementUnit
  }))
  const difficultyMode = parseHikeDifficultyMode(map['difficulty mode']) ?? 'Calculated'
  const difficultyLevel = parseHikeDifficulty(map['difficulty level'])

  return {
    operatorId: map['tour operator id']?.trim() || null,
    operatorName: map['tour operator name']?.trim() || null,
    tripName: map['trip name']?.trim() ?? '',
    description: map['description']?.trim() || null,
    linkToItinerary: map['link to itinerary']?.trim() || null,
    linkToMap: map['link to map']?.trim() || null,
    unGeoscheme: map['un geoscheme']?.trim() || null,
    country: map['country']?.trim() || null,
    stateOrProvince: (map['state'] ?? map['state or province'])?.trim() || null,
    geographicRegion: (map['geogrphic region'] ?? map['geographic region'])?.trim() || null,
    city: map['city']?.trim() || null,
    protectedArea: map['protected area']?.trim() || null,
    tripExternalId: map['trip id']?.trim() || null,
    difficulty: map['difficulty']?.trim() || null,
    difficultyLevel: difficultyMode === 'Calculated' ? null : difficultyLevel,
    difficultyMode,
    planGenerationProfile: parsePlanGenerationProfile(map['plan generation profile']) ?? DEFAULT_PLAN_GENERATION_PROFILE,
    packWeight: map['pack weight']?.trim() || null,
    highestPoint: map['highest point']?.trim() || null,
    aboveEightThousandFeet: parseBooleanCell(map['above 8k feet']),
    tripNumberOfDays: parseIntegerField(map['trip number of days'] ?? ''),
    multipleDailyOptions: parseBooleanCell(map['multiple daily options']),
    isFavorite: parseBooleanCell(map['favorite']) ?? false,
    totalElevationGainMeters: totalElevationGainFeet === undefined
      ? totalElevationGainMetric
      : hikeInputToMetric(totalElevationGainFeet, 'elevation', 'Imperial'),
    totalElevationLossMeters: totalElevationLossFeet === undefined
      ? totalElevationLossMetric
      : hikeInputToMetric(totalElevationLossFeet, 'elevation', 'Imperial'),
    totalDistanceKm: totalDistanceMiles === undefined
      ? totalDistanceMetric
      : hikeInputToMetric(totalDistanceMiles, 'distance', 'Imperial'),
    // buildImportRow clears these once the final profile is known (it can come from the matched hike).
    totalTripDistanceKm: totalTripDistanceMiles === undefined
      ? totalTripDistanceMetric ?? null
      : hikeInputToMetric(totalTripDistanceMiles, 'distance', 'Imperial'),
    totalTripElevationGainMeters: totalTripElevationGainFeet === undefined
      ? totalTripElevationGainMetric ?? null
      : hikeInputToMetric(totalTripElevationGainFeet, 'elevation', 'Imperial'),
    sourceMeasurementUnit,
    itinerary: itineraryWithSource
  }
}

const buildImportRow = (row: ParsedCsvRow, lookups: PopularHikeLookups): ImportPreviewRow => {
  const payload = buildPayloadFromCsv(row.normalized)
  const tripExternalId = payload.tripExternalId ?? undefined
  const compositeKey = makeCompositeKey(payload.tripName)

  let matched: PopularHike | undefined
  if (tripExternalId) {
    matched = lookups.byTripId.get(tripExternalId)
  }
  if (!matched && compositeKey) {
    matched = lookups.byComposite.get(compositeKey)
  }
  if (!Object.prototype.hasOwnProperty.call(row.normalized, 'description') && matched) {
    payload.description = matched.description ?? null
  }
  if (!Object.prototype.hasOwnProperty.call(row.normalized, 'difficulty') && matched) {
    payload.difficulty = matched.difficulty ?? null
  }
  if (!Object.prototype.hasOwnProperty.call(row.normalized, 'difficulty level') && matched) {
    payload.difficultyMode = parseHikeDifficultyMode(row.normalized['difficulty mode']) ?? 'Calculated'
    payload.difficultyLevel = payload.difficultyMode === 'Manual'
      ? parseHikeDifficulty(row.normalized['difficulty level']) ?? DEFAULT_HIKE_DIFFICULTY
      : null
  }
  if (!Object.prototype.hasOwnProperty.call(row.normalized, 'plan generation profile') && matched) {
    payload.planGenerationProfile = matched.planGenerationProfile ?? DEFAULT_PLAN_GENERATION_PROFILE
  }
  const hasTripTotalColumns = TRIP_TOTAL_KEYS.some((key) => Object.prototype.hasOwnProperty.call(row.normalized, key))
  if (payload.planGenerationProfile !== 'LongItinerary') {
    payload.totalTripDistanceKm = null
    payload.totalTripElevationGainMeters = null
  } else if (!hasTripTotalColumns && matched) {
    payload.totalTripDistanceKm = matched.totalTripDistanceKm ?? null
    payload.totalTripElevationGainMeters = matched.totalTripElevationGainMeters ?? null
  }

  const errors: string[] = []
  if (!payload.tripName) {
    errors.push('Missing trip name.')
  }
  if (row.normalized['difficulty level']?.trim() && !parseHikeDifficulty(row.normalized['difficulty level'])) {
    errors.push(`Difficulty level must be ${HIKE_DIFFICULTY_LIST_TEXT}.`)
  }
  const difficultyModeRaw = row.normalized['difficulty mode']?.trim()
  const difficultyMode = parseHikeDifficultyMode(difficultyModeRaw)
  if (difficultyModeRaw && !difficultyMode) {
    errors.push('Difficulty mode must be Legacy, Calculated, or Manual.')
  }
  if (difficultyMode === 'Manual' && !parseHikeDifficulty(row.normalized['difficulty level'])) {
    errors.push('Manual difficulty requires a difficulty level.')
  }
  if (row.normalized['plan generation profile']?.trim() && !parsePlanGenerationProfile(row.normalized['plan generation profile'])) {
    errors.push('Plan generation profile must be Standard, DayHike, or LongItinerary.')
  }
  errors.push(...Object.values(validateLongItinerary(payload)))

  const action: ImportPreviewRow['action'] =
    errors.length > 0 ? 'blocked' : matched ? 'update' : 'create'

  return {
    id: row.id,
    rowNumber: row.rowNumber,
    operatorName: payload.operatorName ?? '',
    tripName: payload.tripName,
    tripExternalId,
    payload,
    matchedHike: matched,
    errors,
    action
  }
}

export const PopularHikesAdminPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const measurements = useMeasurements()
  const isAdmin = user?.role === 'Admin' || user?.role === 'Editor'
  const canManageGeneratedGroups = user?.role === 'Admin'

  const [popularHikes, setPopularHikes] = useState<PopularHike[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<PopularHikeForm>(createEmptyForm())
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showValidation, setShowValidation] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [regeneratingThumbnails, setRegeneratingThumbnails] = useState(false)
  const [regeneratingAllThumbnails, setRegeneratingAllThumbnails] = useState(false)
  const [resettingAll, setResettingAll] = useState(false)
  const [importingRows, setImportingRows] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [parsedRows, setParsedRows] = useState<ParsedCsvRow[]>([])
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(() => new Set())
  const [rowStatuses, setRowStatuses] = useState<Record<string, ImportStatus | undefined>>({})
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [sortConfig, setSortConfig] = useState<HikeSortConfig>({ key: 'tripName', direction: 'asc' })
  const [hikeSearch, setHikeSearch] = useState('')
  const [syncingGroupIds, setSyncingGroupIds] = useState<Set<string>>(() => new Set())
  const [syncingAllGroups, setSyncingAllGroups] = useState(false)
  const [groupSyncResults, setGroupSyncResults] = useState<Record<string, AdminGroupSyncResult>>({})
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const isEditing = editingId !== null
  const editingHike = editingId ? popularHikes.find((hike) => hike.id === editingId) : null
  const isLongItinerary = form.planGenerationProfile === 'LongItinerary'
  const longItineraryErrors = useMemo(() => validateLongItinerary(mapFormToPayload(form)), [form])
  const visibleLongItineraryErrors: LongItineraryErrors = showValidation ? longItineraryErrors : {}
  // Old records may be unitless. Preserve them on unrelated edits, but make
  // newly authored heights unambiguous for both mobile and difficulty scoring.
  const highestPointChanged = form.highestPoint.trim() !== (editingHike?.highestPoint?.trim() ?? '')
  const highestPointError = highestPointChanged && form.highestPoint.trim() && !/\b(?:ft|foot|feet|m|meter|meters|metre|metres)\b/i.test(form.highestPoint)
    ? 'Include ft or m in Highest point, for example 14,270 ft or 4,350 m.'
    : null

  const loadHikes = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getPopularHikes()
      setPopularHikes(data)
    } catch (err) {
      console.error('Failed to load popular hikes', err)
      setError('Unable to load popular hikes right now.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHikes()
  }, [loadHikes])

  const handleResetAll = useCallback(async () => {
    if (!window.confirm('Reset all popular hikes? This will permanently delete every popular hike.')) {
      return
    }

    setResettingAll(true)

    try {
      const result = await resetAllPopularHikes()
      addToast({
        variant: 'success',
        title: 'Popular hikes reset',
        description: `Deleted ${result.deletedCount} hike${result.deletedCount === 1 ? '' : 's'}.`
      })
      await loadHikes()
    } catch (err) {
      console.error('Failed to reset popular hikes', err)
      const description = err instanceof Error ? err.message : 'Please try again.'
      setError(`Unable to reset popular hikes right now. ${description}`)
      addToast({
        variant: 'error',
        title: 'Reset failed',
        description
      })
    } finally {
      setResettingAll(false)
    }
  }, [addToast, loadHikes])

  const lookups = useMemo(() => buildLookups(popularHikes), [popularHikes])

  const sortedHikes = useMemo(() => {
    const query = hikeSearch.trim().toLocaleLowerCase()
    const list = popularHikes.filter((hike) => !query || [hike.tripName, hike.country, hike.stateOrProvince, hike.city, hike.geographicRegion]
      .some((value) => value?.toLocaleLowerCase().includes(query)))
    const { key, direction } = sortConfig
    return list.sort((a, b) => {
      let cmp = 0
      switch (key) {
        case 'tripName':
          cmp = a.tripName.localeCompare(b.tripName)
          break
        case 'country':
          cmp = (a.country ?? '').localeCompare(b.country ?? '')
          break
        case 'tripNumberOfDays':
          cmp = (a.tripNumberOfDays ?? 0) - (b.tripNumberOfDays ?? 0)
          break
        case 'difficulty':
          cmp = (parseHikeDifficulty(a.difficultyLevel) ?? '').localeCompare(parseHikeDifficulty(b.difficultyLevel) ?? '')
          break
        case 'totalDistanceKm':
          cmp = (a.totalDistanceKm ?? 0) - (b.totalDistanceKm ?? 0)
          break
        case 'totalElevationGainMeters':
          cmp = (a.totalElevationGainMeters ?? 0) - (b.totalElevationGainMeters ?? 0)
          break
        case 'totalElevationLossMeters':
          cmp = (a.totalElevationLossMeters ?? 0) - (b.totalElevationLossMeters ?? 0)
          break
        case 'isFavorite':
          cmp = Number(a.isFavorite ?? false) - Number(b.isFavorite ?? false)
          break
      }
      return direction === 'asc' ? cmp : -cmp
    })
  }, [popularHikes, sortConfig, hikeSearch])

  const requestSort = (key: HikeSortKey) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const renderSortIndicator = (key: HikeSortKey) => {
    if (sortConfig.key !== key) {
      return <span className="text-muted-foreground/50" aria-hidden>⇅</span>
    }
    return sortConfig.direction === 'asc' ? <span aria-hidden>↑</span> : <span aria-hidden>↓</span>
  }

  const importPreviewRows = useMemo<ImportPreviewRow[]>(() => {
    return parsedRows.map((row) => {
      const derived = buildImportRow(row, lookups)
      return { ...derived, status: rowStatuses[row.id] }
    })
  }, [parsedRows, lookups, rowStatuses])

  const selectableRows = useMemo(() => importPreviewRows.filter((row) => row.errors.length === 0), [importPreviewRows])
  const selectedCount = useMemo(
    () => selectableRows.filter((row) => selectedRowIds.has(row.id)).length,
    [selectableRows, selectedRowIds]
  )
  const isAllSelected = selectableRows.length > 0 && selectedCount === selectableRows.length

  const resetForm = () => {
    setForm(createEmptyForm())
    setEditingId(null)
    setShowValidation(false)
    setIsModalOpen(false)
  }

  const openAddDialog = () => {
    setForm(createEmptyForm())
    setEditingId(null)
    setShowValidation(false)
    setIsModalOpen(true)
  }

  const handleEdit = (hike: PopularHike) => {
    setEditingId(hike.id)
    setForm(mapHikeToForm(hike))
    setShowValidation(false)
    setIsModalOpen(true)
  }

  const handleInlineUpdate = async (hike: PopularHike, updates: Partial<PopularHikePayload>) => {
    try {
      const current = mapFormToPayload(mapHikeToForm(hike))
      const payload: PopularHikePayload = { ...current, ...updates }
      const updated = await updatePopularHike(hike.id, payload)
      setPopularHikes((prev) => prev.map((h) => (h.id === updated.id ? updated : h)))
    } catch (err) {
      console.error('Failed to update hike', err)
      addToast({ variant: 'error', title: 'Update failed', description: 'Could not save changes.' })
    }
  }

  const handleSubmit = async () => {
    if (!form.tripName.trim()) {
      addToast({ variant: 'error', title: 'Trip name required', description: 'Add a trip name before saving.' })
      return
    }
    if (Object.keys(longItineraryErrors).length > 0) {
      setShowValidation(true)
      addToast({ variant: 'error', title: 'Check the trip totals', description: 'Fix the highlighted Long Itinerary fields before saving.' })
      return
    }
    if (highestPointError) {
      setShowValidation(true)
      return
    }
    setSaving(true)
    try {
      const payload = mapFormToPayload(form)
      if (editingId) {
        const updated = await updatePopularHike(editingId, payload)
        setPopularHikes((prev) => prev.map((hike) => (hike.id === updated.id ? updated : hike)))
        addToast({ variant: 'success', title: 'Hike updated', description: `${updated.tripName} saved successfully.` })
      } else {
        const created = await createPopularHike(payload)
        setPopularHikes((prev) => [...prev, created])
        addToast({ variant: 'success', title: 'Hike created', description: `${created.tripName} added to the library.` })
      }
      resetForm()
    } catch (err) {
      console.error('Failed to save hike', err)
      addToast({ variant: 'error', title: 'Unable to save hike', description: err instanceof Error ? err.message : 'Please try again.' })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteHike = async (hikeId: string) => {
    const hike = popularHikes.find((h) => h.id === hikeId)
    if (!hike) {
      return
    }
    if (!window.confirm(`Delete ${hike.tripName}? This cannot be undone.`)) {
      return
    }
    setDeletingId(hikeId)
    try {
      await deletePopularHike(hikeId)
      setPopularHikes((prev) => prev.filter((h) => h.id !== hikeId))
      if (editingId === hikeId) {
        resetForm()
      }
      addToast({ variant: 'success', title: 'Hike deleted', description: `${hike.tripName} removed.` })
    } catch (err) {
      console.error('Failed to delete hike', err)
      addToast({ variant: 'error', title: 'Unable to delete hike', description: err instanceof Error ? err.message : 'Try again later.' })
    } finally {
      setDeletingId(null)
    }
  }

  const handleSyncGroup = useCallback(async (hike: PopularHike) => {
    if (!canManageGeneratedGroups) {
      return
    }

    setSyncingGroupIds((prev) => {
      const next = new Set(prev)
      next.add(hike.id)
      return next
    })

    try {
      const result = await syncPopularHikeGroup(hike.id)
      setGroupSyncResults((prev) => ({ ...prev, [hike.id]: result }))
      addToast({
        variant: 'success',
        title: result.created ? 'Group created' : 'Group already exists',
        description: `${result.group.name} is ready in mobile groups.`
      })
    } catch (err) {
      console.error('Failed to sync popular hike group', err)
      addToast({
        variant: 'error',
        title: 'Group sync failed',
        description: err instanceof Error ? err.message : 'Please try again.'
      })
    } finally {
      setSyncingGroupIds((prev) => {
        const next = new Set(prev)
        next.delete(hike.id)
        return next
      })
    }
  }, [addToast, canManageGeneratedGroups])

  const handleSyncAllGroups = useCallback(async () => {
    if (!canManageGeneratedGroups || popularHikes.length === 0) {
      return
    }
    if (!window.confirm('Create or sync groups for all popular hikes? Existing generated groups will be reused.')) {
      return
    }

    setSyncingAllGroups(true)

    try {
      const result = await syncAllPopularHikeGroups()
      setGroupSyncResults((prev) => {
        const next = { ...prev }
        result.results.forEach((item) => {
          next[item.sourceId] = item
        })
        return next
      })
      addToast({
        variant: 'success',
        title: 'Popular hike groups synced',
        description: `${result.createdCount} created, ${result.existingCount} already existed.`
      })
    } catch (err) {
      console.error('Failed to sync popular hike groups', err)
      addToast({
        variant: 'error',
        title: 'Group sync failed',
        description: err instanceof Error ? err.message : 'Please try again.'
      })
    } finally {
      setSyncingAllGroups(false)
    }
  }, [addToast, canManageGeneratedGroups, popularHikes.length])

  const addDayToForm = () => {
    setForm((prev) => {
      if (prev.itinerary.length >= MAX_ITINERARY_DAYS) {
        return prev
      }
      return {
        ...prev,
        itinerary: [...prev.itinerary, createEmptyItineraryDay(prev.itinerary.length + 1)]
      }
    })
  }

  const removeDayFromForm = (index: number) => {
    setForm((prev) => {
      if (prev.itinerary.length === 1) {
        return prev
      }
      const next = prev.itinerary.filter((_, idx) => idx !== index).map((day, idx) => ({ ...day, dayNumber: idx + 1 }))
      return { ...prev, itinerary: next }
    })
  }

  const handleItineraryChange = (
    index: number,
    field: keyof ItineraryDay,
    value: string
  ) => {
    const parse = () => {
      if (field === 'dayNumber') {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : index + 1
      }
      if (!value.trim()) {
        return undefined
      }
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : undefined
    }

    setForm((prev) => {
      const next = [...prev.itinerary]
      next[index] = {
        ...next[index],
        [field]: parse()
      }
      return { ...prev, itinerary: next }
    })
  }

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) {
        return
      }
      try {
        const text = await file.text()
        const rows = parseCsvText(text)
        if (rows.length === 0) {
          throw new Error('No data rows were found in the CSV file.')
        }
        const schemaResult = validateCsvSchemaFromMaps(rows.map((row) => row.normalized), {
          expectedType: POPULAR_HIKE_CSV_TYPE,
          currentVersion: POPULAR_HIKE_CSV_VERSION,
          entityName: 'Popular hikes'
        })
        if (schemaResult.warning) {
          addToast({ title: 'Legacy CSV format', description: schemaResult.warning, duration: 9000 })
        }
        const preview = rows.map((row) => buildImportRow(row, lookups))
        const validIds = preview.filter((row) => row.errors.length === 0).map((row) => row.id)
        setParsedRows(rows)
        setRowStatuses({})
        setSelectedRowIds(new Set(validIds))
        setUploadedFileName(file.name)
        addToast({
          variant: 'success',
          title: 'CSV loaded',
          description: `Prepared ${rows.length} row${rows.length === 1 ? '' : 's'} from ${file.name}.`
        })
      } catch (err) {
        console.error('Failed to read CSV', err)
        addToast({ variant: 'error', title: 'Unable to read CSV', description: err instanceof Error ? err.message : 'Ensure the file is accessible.' })
      } finally {
        event.target.value = ''
      }
    },
    [lookups, addToast]
  )

  const handleClearImport = () => {
    setParsedRows([])
    setSelectedRowIds(new Set())
    setRowStatuses({})
    setUploadedFileName(null)
  }

  const toggleSelectAll = () => {
    if (selectableRows.length === 0) {
      return
    }
    if (isAllSelected) {
      setSelectedRowIds(new Set())
    } else {
      setSelectedRowIds(new Set(selectableRows.map((row) => row.id)))
    }
  }

  const toggleRowSelection = (rowId: string, disabled?: boolean) => {
    if (disabled) {
      return
    }
    setSelectedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(rowId)) {
        next.delete(rowId)
      } else {
        next.add(rowId)
      }
      return next
    })
  }

  const handleImportSelected = async () => {
    const rowsToImport = importPreviewRows.filter((row) => selectedRowIds.has(row.id) && row.errors.length === 0)
    if (rowsToImport.length === 0) {
      addToast({ variant: 'error', title: 'No valid rows selected', description: 'Select at least one row before importing.' })
      return
    }
    setImportingRows(true)
    setRowStatuses((prev) => {
      const next = { ...prev }
      rowsToImport.forEach((row) => {
        next[row.id] = { state: 'pending', message: 'Importing…' }
      })
      return next
    })

    let successCount = 0
    let failureCount = 0

    for (const row of rowsToImport) {
      try {
        if (row.matchedHike) {
          const updated = await updatePopularHike(row.matchedHike.id, row.payload)
          setPopularHikes((prev) => prev.map((h) => (h.id === updated.id ? updated : h)))
        } else {
          const created = await createPopularHike(row.payload)
          setPopularHikes((prev) => [...prev, created])
        }
        successCount += 1
        setRowStatuses((prev) => ({ ...prev, [row.id]: { state: 'success', message: row.matchedHike ? 'Updated' : 'Created' } }))
      } catch (err) {
        failureCount += 1
        setRowStatuses((prev) => ({ ...prev, [row.id]: { state: 'error', message: err instanceof Error ? err.message : 'Unexpected error' } }))
      }
    }

    setImportingRows(false)

    if (successCount > 0) {
      addToast({ variant: 'success', title: 'Import complete', description: `${successCount} row${successCount === 1 ? '' : 's'} saved.` })
      await loadHikes()
    }
    if (failureCount > 0) {
      addToast({ variant: 'error', title: 'Some rows failed', description: `${failureCount} row${failureCount === 1 ? '' : 's'} need attention.` })
    }
  }

  const handleExportCsv = async () => {
    setExporting(true)
    try {
      const latest = await getPopularHikes()
      const csvText = buildCsvForPopularHikes(latest)
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      link.download = `hikestrong-popular-hikes-${timestamp}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      setPopularHikes(latest)
      addToast({ variant: 'success', title: 'CSV exported', description: 'Downloaded data for popular hikes.' })
    } catch (err) {
      console.error('Failed to export CSV', err)
      addToast({ variant: 'error', title: 'Unable to export CSV', description: err instanceof Error ? err.message : 'Please try again.' })
    } finally {
      setExporting(false)
    }
  }

  if (!isAdmin) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Admins only"
        description="Only Admin and Editor roles can manage popular hikes."
        className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/60"
      />
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="h-24 animate-pulse rounded-2xl border border-border/50 bg-muted/30" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Card className="border border-rose-200 bg-rose-50/70">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            Unable to load popular hikes
          </CardTitle>
          <CardDescription className="text-rose-600">{error}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button onClick={() => loadHikes()} variant="accent" disabled={resettingAll}>
            Retry
          </Button>
          <Button onClick={() => handleResetAll()} variant="destructive" disabled={resettingAll}>
            {resettingAll ? 'Resetting…' : 'Reset all'}
          </Button>
        </CardContent>
      </Card>
    )
  }

  const hikeEditorContent = (
    <Card className="w-full shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mountain className="h-5 w-5 text-primary" />
              {isEditing ? 'Edit hike' : 'Add a hike'}
            </CardTitle>
            <CardDescription>Fill in the trip details and itinerary.</CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={resetForm} disabled={saving} title="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="trip-name" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trip name</label>
            <Input id="trip-name" value={form.tripName} onChange={(event) => setForm((prev) => ({ ...prev, tripName: event.target.value }))} disabled={saving} />
          </div>
          <div>
            <label htmlFor="trip-external-id" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trip external ID</label>
            <Input id="trip-external-id" value={form.tripExternalId} onChange={(event) => setForm((prev) => ({ ...prev, tripExternalId: event.target.value }))} disabled={saving} />
          </div>
        </div>

        <div>
          <label htmlFor="popular-hike-description" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Popular hike description</label>
          <Textarea
            id="popular-hike-description"
            value={form.description}
            onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            placeholder="Describe what makes this hike unique."
            maxLength={4000}
            disabled={saving}
          />
          <p className="mt-1 text-xs text-muted-foreground">Shown on the mobile hike detail. Leave blank to use the generic hike copy.</p>
        </div>

        {isEditing && (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hero images</p>
            <div className="flex flex-wrap gap-3">
              {form.heroImageUrls.length === 0 && (
                <p className="text-xs text-muted-foreground">No hero images yet.</p>
              )}
              {form.heroImageUrls.map((url, index) => (
                <div key={url} className="group relative flex flex-col items-start gap-1">
                  <img src={url} alt={`Hero ${index + 1}`} className="h-20 w-32 rounded-md object-cover border border-border/60" />
                  <button
                    type="button"
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose-500 text-white opacity-0 transition-opacity hover:bg-rose-600 group-hover:opacity-100"
                    title="Delete image"
                    onClick={async () => {
                      if (!editingId) return
                      if (!window.confirm('Delete this hero image? This cannot be undone.')) return
                      try {
                        const updated = await deletePopularHikeHeroImage(editingId, url)
                        setPopularHikes((prev) => prev.map((h) => (h.id === updated.id ? updated : h)))
                        setForm(mapHikeToForm(updated))
                        addToast({ variant: 'success', title: 'Image deleted', description: 'Hero image removed successfully.' })
                      } catch (err) {
                        console.error('Failed to delete hero image', err)
                        addToast({ variant: 'error', title: 'Delete failed', description: 'Unable to delete hero image. Please try again.' })
                      }
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1">
                <label htmlFor="hero-image-upload" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Upload new hero image</label>
                <input
                  id="hero-image-upload"
                  type="file"
                  accept="image/*"
                  className="mt-1 block w-full text-xs"
                  onChange={async (event) => {
                    const file = event.target.files?.[0]
                    if (!file || !editingId) return
                    try {
                      const updated = await uploadPopularHikeHeroImage(editingId, file)
                      setPopularHikes((prev) => prev.map((h) => (h.id === updated.id ? updated : h)))
                      setForm(mapHikeToForm(updated))
                      addToast({ variant: 'success', title: 'Image uploaded', description: 'Hero image added successfully.' })
                    } catch (err) {
                      console.error('Failed to upload hero image', err)
                      addToast({ variant: 'error', title: 'Upload failed', description: 'Unable to upload hero image. Please try again.' })
                    } finally {
                      event.target.value = ''
                    }
                  }}
                />
              </div>
              {form.heroImageUrls.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={regeneratingThumbnails}
                  onClick={async () => {
                    if (!editingId) return
                    setRegeneratingThumbnails(true)
                    try {
                      const result = await regeneratePopularHikeThumbnails(editingId)
                      addToast({ variant: 'success', title: 'Thumbnails regenerated', description: `Processed: ${result.processed}, Skipped: ${result.skipped}, Failed: ${result.failed}` })
                    } catch (err) {
                      console.error('Failed to regenerate thumbnails', err)
                      addToast({ variant: 'error', title: 'Regeneration failed', description: 'Unable to regenerate thumbnails. Please try again.' })
                    } finally {
                      setRegeneratingThumbnails(false)
                    }
                  }}
                >
                  {regeneratingThumbnails ? (
                    <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Regenerating...</>
                  ) : (
                    'Regenerate Thumbnails'
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <input
            id="modal-favorite"
            type="checkbox"
            className="h-4 w-4 accent-emerald-600"
            checked={form.isFavorite}
            onChange={(event) => setForm((prev) => ({ ...prev, isFavorite: event.target.checked }))}
            disabled={saving}
          />
          <label htmlFor="modal-favorite" className="text-sm font-medium text-foreground">Mark as favorite (eligible for random picks)</label>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="modal-link-itinerary" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Link to itinerary</label>
            <Input id="modal-link-itinerary" value={form.linkToItinerary} onChange={(event) => setForm((prev) => ({ ...prev, linkToItinerary: event.target.value }))} placeholder="https://…" disabled={saving} />
          </div>
          <div>
            <label htmlFor="modal-link-map" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Link to map</label>
            <Input id="modal-link-map" value={form.linkToMap} onChange={(event) => setForm((prev) => ({ ...prev, linkToMap: event.target.value }))} placeholder="https://…" disabled={saving} />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="modal-un-geoscheme" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">UN geoscheme</label>
            <Input id="modal-un-geoscheme" value={form.unGeoscheme} onChange={(event) => setForm((prev) => ({ ...prev, unGeoscheme: event.target.value }))} disabled={saving} />
          </div>
          <div>
            <label htmlFor="modal-country" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Country</label>
            <Input id="modal-country" value={form.country} onChange={(event) => setForm((prev) => ({ ...prev, country: event.target.value }))} disabled={saving} />
          </div>
          <div>
            <label htmlFor="modal-state" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">State / Province</label>
            <Input id="modal-state" value={form.stateOrProvince} onChange={(event) => setForm((prev) => ({ ...prev, stateOrProvince: event.target.value }))} disabled={saving} />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="modal-region" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Region</label>
            <Input id="modal-region" value={form.geographicRegion} onChange={(event) => setForm((prev) => ({ ...prev, geographicRegion: event.target.value }))} disabled={saving} />
          </div>
          <div>
            <label htmlFor="modal-city" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">City</label>
            <Input id="modal-city" value={form.city} onChange={(event) => setForm((prev) => ({ ...prev, city: event.target.value }))} disabled={saving} />
          </div>
          <div>
            <label htmlFor="modal-protected-area" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Protected area</label>
            <Input id="modal-protected-area" value={form.protectedArea} onChange={(event) => setForm((prev) => ({ ...prev, protectedArea: event.target.value }))} disabled={saving} />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label htmlFor="modal-difficulty-mode" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Difficulty mode</label>
            <select
              id="modal-difficulty-mode"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.difficultyMode}
              onChange={(event) => setForm((prev) => ({ ...prev, difficultyMode: event.target.value as PopularHikeForm['difficultyMode'] }))}
              disabled={saving}
            >
              {form.difficultyMode === 'Legacy' && <option value="Legacy">Legacy (choose a mode)</option>}
              <option value="Calculated">Calculated</option>
              <option value="Manual">Manual</option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">Calculated ratings update from the itinerary and highest point.</p>
          </div>
          <div>
            <label htmlFor="modal-difficulty-level" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Difficulty</label>
            <select
              id="modal-difficulty-level"
              aria-label="Manual difficulty"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              value={form.difficultyLevel}
              onChange={(event) => setForm((prev) => ({ ...prev, difficultyLevel: event.target.value as PopularHikeForm['difficultyLevel'] }))}
              disabled={saving || form.difficultyMode !== 'Manual'}
            >
              {HIKE_DIFFICULTY_OPTIONS.map((difficulty) => (
                <option key={difficulty} value={difficulty}>{formatHikeDifficulty(difficulty)}</option>
              ))}
            </select>
            {form.difficultyMode !== 'Manual' && (
              <p className="mt-1 text-xs text-muted-foreground">Effective rating: {formatHikeDifficulty(parseHikeDifficulty(editingHike?.difficultyLevel)) ?? 'Unrated'}</p>
            )}
          </div>
          <div>
            <label htmlFor="modal-plan-generation-profile" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Training plan profile</label>
            <select
              id="modal-plan-generation-profile"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.planGenerationProfile}
              onChange={(event) => setForm((prev) => ({ ...prev, planGenerationProfile: event.target.value as PlanGenerationProfile }))}
              disabled={saving}
            >
              {PLAN_GENERATION_PROFILES.map((profile) => (
                <option key={profile} value={profile}>{planGenerationProfileLabel(profile)}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {isLongItinerary
                ? 'For thru-hikes: plans use the itemized days; trip totals cover the whole trip.'
                : 'Day hike removes the standard weekly distance and elevation floors.'}
            </p>
          </div>
          <div>
            <label htmlFor="modal-pack-weight" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pack weight</label>
            <Input id="modal-pack-weight" value={form.packWeight} onChange={(event) => setForm((prev) => ({ ...prev, packWeight: event.target.value }))} disabled={saving} />
          </div>
          {editingHike?.difficultyCalculation && (
            <div className="md:col-span-4 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              Score {editingHike.difficultyCalculation.totalScore ?? '—'} · {editingHike.difficultyCalculation.formulaType ?? 'Unknown formula'} · {editingHike.difficultyCalculation.hikingDays} hiking day{editingHike.difficultyCalculation.hikingDays === 1 ? '' : 's'} · formula {editingHike.difficultyCalculation.version}
            </div>
          )}
          <div>
            <label htmlFor="modal-highest-point" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Highest point</label>
            <Input
              id="modal-highest-point"
              value={form.highestPoint}
              onChange={(event) => setForm((prev) => ({ ...prev, highestPoint: event.target.value }))}
              placeholder="14,270 ft or 4,350 m"
              aria-describedby="modal-highest-point-help"
              aria-invalid={Boolean(showValidation && highestPointError)}
              disabled={saving}
            />
            <p id="modal-highest-point-help" className={`mt-1 text-xs ${showValidation && highestPointError ? 'text-rose-600' : 'text-muted-foreground'}`}>
              {showValidation && highestPointError ? highestPointError : 'Include the unit (ft or m). The app converts this to each hiker’s preferred units.'}
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label htmlFor="modal-above-8k" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Above 8k ft?</label>
            <select
              id="modal-above-8k"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.aboveEightThousandFeet}
              onChange={(event) => setForm((prev) => ({ ...prev, aboveEightThousandFeet: event.target.value as '' | 'yes' | 'no' }))}
              disabled={saving}
            >
              <option value="">Unknown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div>
            <label htmlFor="modal-trip-days" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trip days</label>
            <Input
              id="modal-trip-days"
              type="number"
              value={form.tripNumberOfDays}
              onChange={(event) => setForm((prev) => ({ ...prev, tripNumberOfDays: event.target.value }))}
              disabled={saving}
              aria-invalid={Boolean(visibleLongItineraryErrors.tripNumberOfDays)}
            />
            {visibleLongItineraryErrors.tripNumberOfDays && <p className="mt-1 text-xs text-rose-600">{visibleLongItineraryErrors.tripNumberOfDays}</p>}
          </div>
          <div>
            <label htmlFor="modal-multiple-options" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Multiple daily options</label>
            <select
              id="modal-multiple-options"
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              value={form.multipleDailyOptions}
              onChange={(event) => setForm((prev) => ({ ...prev, multipleDailyOptions: event.target.value as '' | 'yes' | 'no' }))}
              disabled={saving}
            >
              <option value="">Unknown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div>
            <label htmlFor="modal-total-distance" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total distance ({measurements.unitLabel('distance')})</label>
            <MeasurementInput
              id="modal-total-distance"
              kind="distance"
              valueMetric={parseNumberField(form.totalDistance)}
              onValueMetricChange={(value) => setForm((prev) => ({ ...prev, totalDistance: metricFormString(value) }))}
              disabled={saving}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="modal-total-gain" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total gain ({measurements.unitLabel('elevation')})</label>
            <MeasurementInput
              id="modal-total-gain"
              kind="elevation"
              valueMetric={parseNumberField(form.totalElevationGain)}
              onValueMetricChange={(value) => setForm((prev) => ({ ...prev, totalElevationGain: metricFormString(value) }))}
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="modal-total-loss" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total loss ({measurements.unitLabel('elevation')})</label>
            <MeasurementInput
              id="modal-total-loss"
              kind="elevation"
              valueMetric={parseNumberField(form.totalElevationLoss)}
              onValueMetricChange={(value) => setForm((prev) => ({ ...prev, totalElevationLoss: metricFormString(value) }))}
              disabled={saving}
            />
          </div>
        </div>

        {isLongItinerary && (
          <div className="space-y-2">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="modal-total-trip-distance" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total trip distance ({measurements.unitLabel('distance')})</label>
                <MeasurementInput
                  id="modal-total-trip-distance"
                  kind="distance"
                  valueMetric={parseNumberField(form.totalTripDistance)}
                  onValueMetricChange={(value) => setForm((prev) => ({ ...prev, totalTripDistance: metricFormString(value) }))}
                  disabled={saving}
                  aria-invalid={Boolean(visibleLongItineraryErrors.totalTripDistanceKm)}
                />
                {visibleLongItineraryErrors.totalTripDistanceKm && <p className="mt-1 text-xs text-rose-600">{visibleLongItineraryErrors.totalTripDistanceKm}</p>}
              </div>
              <div>
                <label htmlFor="modal-total-trip-gain" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total trip elevation gain ({measurements.unitLabel('elevation')})</label>
                <MeasurementInput
                  id="modal-total-trip-gain"
                  kind="elevation"
                  valueMetric={parseNumberField(form.totalTripElevationGain)}
                  onValueMetricChange={(value) => setForm((prev) => ({ ...prev, totalTripElevationGain: metricFormString(value) }))}
                  disabled={saving}
                  aria-invalid={Boolean(visibleLongItineraryErrors.totalTripElevationGainMeters)}
                />
                {visibleLongItineraryErrors.totalTripElevationGainMeters && <p className="mt-1 text-xs text-rose-600">{visibleLongItineraryErrors.totalTripElevationGainMeters}</p>}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Only the first {form.itinerary.length} itinerary day{form.itinerary.length === 1 ? ' is' : 's are'} itemized; these totals cover the whole trip.
            </p>
          </div>
        )}

        {(() => {
          const computed = sumItinerary(form.itinerary)
          if (!computed.distance && !computed.gain && !computed.loss) return null
          return (
            <p className="text-xs text-muted-foreground">
              Computed from itinerary: {measurements.format(computed.distance, 'distance')} distance, {measurements.format(computed.gain, 'elevation')} gain, {measurements.format(computed.loss, 'elevation')} loss. These will override the manual values above on save.
            </p>
          )
        })()}

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Itinerary</p>
            <Button size="sm" variant="outline" onClick={addDayToForm} disabled={form.itinerary.length >= MAX_ITINERARY_DAYS || saving}>
              <Plus className="mr-1 h-4 w-4" /> Add day
            </Button>
          </div>
          {visibleLongItineraryErrors.itinerary && <p className="text-xs text-rose-600">{visibleLongItineraryErrors.itinerary}</p>}

          <div className="grid gap-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground md:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
            <span>Day</span>
            <span>{measurements.unitLabel('distance')}</span>
            <span>Gain ({measurements.unitLabel('elevation')})</span>
            <span>Loss ({measurements.unitLabel('elevation')})</span>
            <span className="hidden md:block text-right">Actions</span>
          </div>

          {form.itinerary.map((day, index) => (
            <div key={`day-${index}`} className="grid gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
              <Input type="number" min={1} max={MAX_ITINERARY_DAYS} value={day.dayNumber} onChange={(event) => handleItineraryChange(index, 'dayNumber', event.target.value)} disabled={saving} />
              <MeasurementInput kind="distance" placeholder={measurements.unitLabel('distance')} valueMetric={day.distanceKm ?? null} onValueMetricChange={(value) => handleItineraryChange(index, 'distanceKm', metricFormString(value))} disabled={saving} />
              <MeasurementInput kind="elevation" placeholder={`Gain (${measurements.unitLabel('elevation')})`} valueMetric={day.elevationGainMeters ?? null} onValueMetricChange={(value) => handleItineraryChange(index, 'elevationGainMeters', metricFormString(value))} disabled={saving} />
              <MeasurementInput kind="elevation" placeholder={`Loss (${measurements.unitLabel('elevation')})`} valueMetric={day.elevationLossMeters ?? null} onValueMetricChange={(value) => handleItineraryChange(index, 'elevationLossMeters', metricFormString(value))} disabled={saving} />
              <div className="flex items-center justify-end">
                <Button size="icon" variant="ghost" onClick={() => removeDayFromForm(index)} disabled={form.itinerary.length === 1 || saving}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => handleSubmit()} disabled={saving || !form.tripName.trim()}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create hike'}
          </Button>
          <Button variant="ghost" onClick={resetForm} disabled={saving}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6">
      <header className="workspace-heading">
        <div>
          <h1>Popular hikes</h1>
          <p>Curated hikes and itineraries for the HikeStrong app.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManageGeneratedGroups ? (
            <Button
              variant="outline"
              onClick={handleSyncAllGroups}
              disabled={syncingAllGroups || popularHikes.length === 0}
              className="gap-2"
            >
              {syncingAllGroups ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
              Sync groups
            </Button>
          ) : null}
          <Button onClick={openAddDialog} className="order-first gap-2 sm:order-last">
            <Plus className="h-4 w-4" />
            Add hike
          </Button>
          <Button
            variant="outline"
            disabled={regeneratingAllThumbnails}
            onClick={async () => {
              if (!window.confirm('Regenerate thumbnails for ALL popular hikes? This may take a while.')) return
              setRegeneratingAllThumbnails(true)
              try {
                const result = await regenerateAllPopularHikeThumbnails()
                addToast({
                  variant: 'success',
                  title: 'All thumbnails regenerated',
                  description: `Processed: ${result.processed}, Skipped: ${result.skipped}, Failed: ${result.failed}`
                })
              } catch (err) {
                console.error('Failed to regenerate all thumbnails', err)
                addToast({
                  variant: 'error',
                  title: 'Regeneration failed',
                  description: 'Unable to regenerate thumbnails. Please try again.'
                })
              } finally {
                setRegeneratingAllThumbnails(false)
              }
            }}
          >
            {regeneratingAllThumbnails ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Regenerating All...
              </>
            ) : (
              'Regenerate All Thumbnails'
            )}
          </Button>
        </div>
      </header>

      {isModalOpen ? (
        <Dialog open={isModalOpen} onOpenChange={(isOpen) => !isOpen && resetForm()}>
          <DialogContent>
            <DialogTitle className="sr-only">{isEditing ? 'Edit hike' : 'Add a hike'}</DialogTitle>
            <DialogDescription className="sr-only">Use this form to update hike details.</DialogDescription>
            {hikeEditorContent}
          </DialogContent>
        </Dialog>
      ) : null}

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Hike library</CardTitle>
              <CardDescription>{hikeSearch.trim() ? `${sortedHikes.length} of ${popularHikes.length} hikes` : `${popularHikes.length} curated adventure${popularHikes.length === 1 ? '' : 's'}.`}</CardDescription>
            </div>
            <Input aria-label="Search popular hikes" placeholder="Search hikes or locations…" value={hikeSearch} onChange={(event) => setHikeSearch(event.target.value)} className="sm:w-72" />
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {popularHikes.length === 0 ? (
            <EmptyState
              icon={Mountain}
              title="No popular hikes yet"
              description="Create your first featured hike to inspire the community."
              className="border border-dashed border-border/60 bg-muted/20"
            >
              <Button onClick={openAddDialog} className="gap-2">
                <Plus className="h-4 w-4" />
                Add hike
              </Button>
            </EmptyState>
          ) : sortedHikes.length === 0 ? (
            <EmptyState icon={Mountain} title="No matching hikes" description="Try another hike name or location." action={{ label: 'Clear search', onClick: () => setHikeSearch('') }} />
          ) : (
            <table className="workspace-table w-full min-w-[1050px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="cursor-pointer select-none pb-3" onClick={() => requestSort('tripName')}>
                    <span className="inline-flex items-center gap-1">Trip {renderSortIndicator('tripName')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3" onClick={() => requestSort('country')}>
                    <span className="inline-flex items-center gap-1">Location {renderSortIndicator('country')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3" onClick={() => requestSort('tripNumberOfDays')}>
                    <span className="inline-flex items-center gap-1">Days {renderSortIndicator('tripNumberOfDays')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3" onClick={() => requestSort('difficulty')}>
                    <span className="inline-flex items-center gap-1">Difficulty {renderSortIndicator('difficulty')}</span>
                  </th>
                  <th className="pb-3">Plan profile</th>
                  <th className="cursor-pointer select-none pb-3 text-right" onClick={() => requestSort('totalDistanceKm')}>
                    <span className="inline-flex items-center gap-1">Dist ({measurements.unitLabel('distance')}) {renderSortIndicator('totalDistanceKm')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3 text-right" onClick={() => requestSort('totalElevationGainMeters')}>
                    <span className="inline-flex items-center gap-1">Gain ({measurements.unitLabel('elevation')}) {renderSortIndicator('totalElevationGainMeters')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3 text-right" onClick={() => requestSort('totalElevationLossMeters')}>
                    <span className="inline-flex items-center gap-1">Loss ({measurements.unitLabel('elevation')}) {renderSortIndicator('totalElevationLossMeters')}</span>
                  </th>
                  <th className="cursor-pointer select-none pb-3" onClick={() => requestSort('isFavorite')}>
                    <span className="inline-flex items-center gap-1">Favorite {renderSortIndicator('isFavorite')}</span>
                  </th>
                  {canManageGeneratedGroups ? <th className="pb-3 text-right">Group</th> : null}
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedHikes.map((hike) => {
                  const syncResult = groupSyncResults[hike.id]
                  const isSyncingGroup = syncingAllGroups || syncingGroupIds.has(hike.id)

                  return (
                    <tr key={hike.id} className="border-b border-border/60 last:border-b-0">
                      <td className="py-3 pr-4 font-medium text-foreground">
                        <div className="flex items-center gap-3">
                          <HikeThumbnail source={hike.thumbnailUrl || hike.heroImageUrls?.[0]} />
                          <div className="min-w-0">
                            <span className="line-clamp-2 max-w-[220px] leading-snug" title={hike.tripName}>{hike.tripName}</span>
                            {hike.linkToItinerary ? <a href={hike.linkToItinerary} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-normal text-primary hover:underline" aria-label={`View itinerary for ${hike.tripName}`}>Itinerary <span aria-hidden="true">↗</span></a> : null}
                          </div>
                        </div>
                      </td>
                      <td className="max-w-[170px] py-3 pr-3 text-muted-foreground"><span className="line-clamp-2 leading-snug" title={[hike.city, hike.stateOrProvince, hike.country].filter(Boolean).join(', ')}>{[hike.city, hike.stateOrProvince, hike.country].filter(Boolean).join(', ') || '—'}</span></td>
                      <td className="py-3 font-mono text-xs">{hike.tripNumberOfDays ?? hike.itinerary.length}</td>
                      <td className="py-3">{formatHikeDifficulty(parseHikeDifficulty(hike.difficultyLevel)) ?? 'Unrated'}</td>
                      <td className="py-3">{planGenerationProfileLabel(hike.planGenerationProfile ?? DEFAULT_PLAN_GENERATION_PROFILE)}</td>
                      <td className="whitespace-nowrap py-3 pl-2 text-right font-mono text-xs"><MeasurementValue valueMetric={hike.totalDistanceKm} kind="distance" /></td>
                      <td className="whitespace-nowrap py-3 pl-2 text-right font-mono text-xs"><MeasurementValue valueMetric={hike.totalElevationGainMeters} kind="elevation" /></td>
                      <td className="whitespace-nowrap py-3 pl-2 text-right font-mono text-xs"><MeasurementValue valueMetric={hike.totalElevationLossMeters} kind="elevation" /></td>
                      <td className="py-3">
                        <button
                          type="button"
                          onClick={() => handleInlineUpdate(hike, { isFavorite: !hike.isFavorite })}
                          className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-1 text-xs font-medium transition-colors ${
                            hike.isFavorite
                              ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                              : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'
                          }`}
                          title="Toggle favorite"
                        >
                          <Star className="h-3 w-3" /> {hike.isFavorite ? 'Favorite' : '—'}
                        </button>
                      </td>
                      {canManageGeneratedGroups ? (
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {syncResult ? (
                              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                                <CheckCircle2 className="h-3 w-3" />
                                {syncResult.created ? 'Created' : 'Synced'}
                              </span>
                            ) : null}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSyncGroup(hike)}
                              disabled={isSyncingGroup}
                              className="gap-1"
                              aria-label={`Sync group for ${hike.tripName}`}
                            >
                              {isSyncingGroup ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                              Sync
                            </Button>
                          </div>
                        </td>
                      ) : null}
                      <td className="py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(hike)} className="gap-1">
                            <Pencil className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() => handleDeleteHike(hike.id)}
                            disabled={deletingId === hike.id}
                          >
                            {deletingId === hike.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card className="border border-dashed border-border/60">
        <CardHeader>
          <CardTitle>Import / export CSV</CardTitle>
          <CardDescription>Use the shared spreadsheet to update popular hikes in bulk.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 rounded-2xl border border-dashed border-border/70 bg-muted/30 p-4 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-muted-foreground">
              <p>Choose a CSV (for example <code className="text-xs">/Users/chris/Downloads/popular-hikes.csv</code>) to preview and import.</p>
              {uploadedFileName && (
                <p className="mt-1 text-xs text-foreground">
                  Loaded <span className="font-medium">{uploadedFileName}</span> &middot; {parsedRows.length} row{parsedRows.length === 1 ? '' : 's'}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importingRows}>
                <Upload className="mr-2 h-4 w-4" />
                Select CSV
              </Button>
              <Button variant="outline" onClick={handleExportCsv} disabled={exporting}>
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting…
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </>
                )}
              </Button>
              {parsedRows.length > 0 && (
                <Button variant="ghost" onClick={handleClearImport} disabled={importingRows}>
                  Clear
                </Button>
              )}
            </div>
          </div>

          {parsedRows.length > 0 && (
            <div className="space-y-3 rounded-2xl border border-border/60 bg-card/80 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {selectedCount} of {selectableRows.length} row{selectableRows.length === 1 ? '' : 's'} selected
                  </p>
                  <p className="text-xs text-muted-foreground">Rows missing a trip name stay disabled.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={toggleSelectAll} disabled={selectableRows.length === 0 || importingRows}>
                    {isAllSelected ? 'Clear selection' : 'Select all'}
                  </Button>
                  <Button size="sm" onClick={handleImportSelected} disabled={selectedCount === 0 || importingRows}>
                    {importingRows ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Importing…
                      </>
                    ) : (
                      <>Import {selectedCount} row{selectedCount === 1 ? '' : 's'}</>
                    )}
                  </Button>
                </div>
              </div>

              <div className="overflow-auto rounded-2xl border border-border/50">
                <table className="min-w-full divide-y divide-border/60 text-left text-sm">
                  <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border accent-foreground"
                          checked={isAllSelected && selectableRows.length > 0}
                          onChange={toggleSelectAll}
                          disabled={selectableRows.length === 0 || importingRows}
                        />
                      </th>
                      <th className="w-16 px-3 py-2">Row</th>
                      <th className="px-3 py-2">Trip</th>
                      <th className="px-3 py-2">Action</th>
                      <th className="w-48 px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40 bg-white/80 text-foreground">
                    {importPreviewRows.map((row) => {
                      const selectable = row.errors.length === 0
                      const checked = selectable && selectedRowIds.has(row.id)
                      return (
                        <tr key={row.id} className={!selectable ? 'bg-rose-50/70' : undefined}>
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-border accent-foreground"
                              checked={checked}
                              disabled={!selectable || importingRows}
                              onChange={() => toggleRowSelection(row.id, !selectable || importingRows)}
                            />
                          </td>
                          <td className="px-3 py-3 text-xs text-muted-foreground">#{row.rowNumber}</td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-foreground">{row.tripName || '—'}</p>
                            {row.tripExternalId && <p className="text-xs text-muted-foreground">CSV trip id: {row.tripExternalId}</p>}
                            {row.operatorName && <p className="text-xs text-muted-foreground">Operator (ignored): {row.operatorName}</p>}
                            {row.errors.length > 0 && <p className="mt-1 text-xs text-rose-600">{row.errors.join(' ')}</p>}
                          </td>
                          <td className="px-3 py-3">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                row.action === 'update'
                                  ? 'bg-sky-50 text-sky-700'
                                  : row.action === 'create'
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-rose-50 text-rose-700'
                              }`}
                            >
                              {row.action === 'update' ? 'Update' : row.action === 'create' ? 'Create' : 'Blocked'}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            {row.status?.state === 'success' && (
                              <div className="flex items-center gap-2 text-emerald-600">
                                <CheckCircle2 className="h-4 w-4" />
                                <span className="text-xs font-medium">Imported</span>
                              </div>
                            )}
                            {row.status?.state === 'error' && (
                              <div className="flex items-center gap-2 text-rose-600">
                                <XCircle className="h-4 w-4" />
                                <span className="text-xs font-medium">Failed</span>
                              </div>
                            )}
                            {row.status?.state === 'pending' && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-xs font-medium">Importing…</span>
                              </div>
                            )}
                            {!row.status && <span className="text-xs text-muted-foreground">—</span>}
                            {row.status?.message && <p className="text-xs text-muted-foreground">{row.status.message}</p>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
